import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, RefreshCw, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { InfoHint } from "@/components/InfoHint";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { apiErrorMessage } from "@/lib/api-error";
import { formatDateTime } from "@/lib/format";
import { useBooks } from "./BooksProvider";
import { useLoad } from "./useLoad";
import { PostingStatusBadge } from "./shared";
import type { JournalSourceType, Posting } from "./types";

/**
 * S6: whether a document is in the books, like the accounting sync card. Renders nothing without an open book,
 * so the host shows it only behind the `books` module and needs no other check.
 */
export function BooksDocumentCard({ sourceType, sourceId }: { sourceType: JournalSourceType; sourceId: string }) {
  const { books, canManage } = useBooks();
  const [retrying, setRetrying] = useState(false);
  const loaded = useLoad<Posting | null>(
    async () => {
      const r = await books.GET("/v1/books/current/postings", {
        params: { query: { SourceType: sourceType, SourceId: sourceId, Limit: 1 } },
      });
      return { ...r, data: r.data ? (r.data.data?.[0] ?? null) : undefined };
    },
    [books, sourceType, sourceId],
    { notFound: undefined },
  );

  // No book (404), or the read failed: the card is a side panel, so it steps aside rather than shout.
  if (loaded.data === undefined) return null;
  const posting = loaded.data;

  async function retry() {
    if (!posting?.id) return;
    setRetrying(true);
    const { error } = await books.POST("/v1/books/current/postings/{id}/retry", { params: { path: { id: posting.id } } });
    if (error) {
      setRetrying(false);
      toast.error(apiErrorMessage(error, "Couldn't retry the posting."));
      return;
    }
    // Posting runs in the background: give it a moment, then show where it got to.
    await new Promise((r) => setTimeout(r, 1500));
    await loaded.reload();
    setRetrying(false);
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-base">
          Books
          <InfoHint label="About the books">
            Dolfin's own double-entry books. Posting here is separate from the push to your accounting platform: one never
            blocks the other.
          </InfoHint>
        </CardTitle>
        {canManage && posting?.status === "Failed" ? (
          <Button size="sm" variant="outline" disabled={retrying} onClick={retry}>
            {retrying ? <Spinner className="size-4" /> : <RefreshCw className="size-4" />} Retry
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <PostingStatusBadge posting={posting ?? { status: undefined }} className="text-sm" />
          {posting?.lastAttemptAt ? (
            <span className="text-xs text-muted-foreground">{formatDateTime(posting.lastAttemptAt)}</span>
          ) : null}
        </div>
        {posting?.status === "Failed" && posting.reason ? (
          <p className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
            <TriangleAlert className="mt-px size-3.5 shrink-0" />
            <span>{posting.reason}</span>
          </p>
        ) : posting?.status === "Skipped" && posting.reason ? (
          <p className="text-xs text-muted-foreground">{posting.reason}</p>
        ) : !posting ? (
          <p className="text-xs text-muted-foreground">
            Not in the books yet. It posts once issued, approved or coded, unless it is dated before the books start.
          </p>
        ) : null}
        {posting?.entryId ? (
          <Link to={`/books/ledger?entry=${posting.entryId}`} className="inline-flex items-center gap-1 text-xs font-medium underline-offset-4 hover:underline">
            Open the journal entry <ArrowRight className="size-3.5" />
          </Link>
        ) : null}
      </CardContent>
    </Card>
  );
}
